<template>
  <div class="page-container">
    <!-- Marco de pagina del portal: mismo contenedor y mismo titulo que el
         resto del admin. -->
    <div class="page-title-container d-flex flex-wrap align-items-center justify-content-between">
      <h1 class="page-title mb-0">Dashboard</h1>

      <div class="d-flex flex-wrap align-items-center">
        <button v-for="c in ciudadesFiltro" :key="c.id" type="button"
          class="btn btn-sm mr-1 mb-1" :class="cityId === c.id ? 'btn-primary' : 'btn-outline-secondary'"
          @click="setCity(c.id)">
          {{ c.name }}
        </button>
        <select v-model.number="month" class="form-control form-control-sm w-auto ml-2 mb-1" @change="loadPeriodo">
          <option v-for="(m, i) in mesesNombres" :key="i" :value="i + 1">{{ m }}</option>
        </select>
        <select v-model.number="year" class="form-control form-control-sm w-auto ml-1 mb-1" @change="loadPeriodo">
          <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
        </select>
        <button type="button" class="button-tooltip text-dark font-weight-bold ml-1 mb-1" :disabled="loading"
          v-b-tooltip.hover :title="loading ? 'Actualizando…' : 'Actualizado ' + horaActualizacion" @click="loadAll()">
          <i class="ri-loop-right-line"></i>
        </button>
      </div>
    </div>

    <!-- Un fallo de carga no puede ser invisible: con el refresco automático
         cada 5 minutos, la pantalla se quedaría con los números de hace horas
         y nadie lo notaría. Las seis llamadas van juntas: si una falla, ninguna
         de las seis secciones se actualizó. -->
    <div v-if="errorCarga" class="alert alert-danger d-flex align-items-center py-2 px-3 mb-3">
      <i class="ri-error-warning-line mr-2"></i>
      <span class="flex-grow-1">
        No se pudieron actualizar los datos.
        <template v-if="horaActualizacion">Lo que se ve es de las {{ horaActualizacion }}</template>
        <template v-else>La pantalla todavía no tiene datos.</template>
      </span>
      <button type="button" class="btn btn-sm btn-danger ml-2" :disabled="loading" @click="loadAll()">
        Reintentar
      </button>
    </div>

    <!-- Si el API de ciudades falla no queda ninguna seleccionada, y consultar
         sin city_id mostraría el total de las tres: justo lo que se sacó. -->
    <div v-if="sinCiudades" class="alert alert-warning d-flex align-items-center py-2 px-3 mb-3">
      <i class="ri-alert-line mr-2"></i>
      <span class="flex-grow-1">No se pudieron cargar las ciudades. El dashboard se muestra por ciudad, así que no hay nada que consultar todavía.</span>
      <button type="button" class="btn btn-sm btn-warning ml-2" :disabled="loading" @click="reintentarCiudades">
        Reintentar
      </button>
    </div>

    <!-- Sin TC no se bloquea la pantalla: viajes, conductores, alertas y
         proximos no dependen del tipo de cambio. Solo los montos se degradan. -->
    <div v-if="!hayConversion" class="alert d-flex align-items-center py-2 px-3 mb-3"
      :class="fallaTC ? 'alert-danger' : 'alert-warning'">
      <i class="mr-2" :class="fallaTC ? 'ri-error-warning-line' : 'ri-alert-line'"></i>
      <template v-if="fallaTC">
        <!-- Sin botón: esto no se arregla cargando un TC. Ofrecerlo mandaría a
             cargar uno que ya existe. -->
        <span class="flex-grow-1">
          No se pudo consultar el tipo de cambio. Los montos no se muestran en soles hasta que la consulta responda.
        </span>
      </template>
      <template v-else>
        <span class="flex-grow-1">No hay tipo de cambio cargado. Los montos no se pueden mostrar en soles.</span>
        <!-- Sin v-if de rol: al dashboard solo llegan SUPER_ADMINISTRATOR y
             ADMINISTRATOR — el grupo admin del API — y los dos pueden cargar
             el TC. Acá había un "pedíselo a un super administrador" para el
             resto; con este cambio ese resto no existe. -->
        <button type="button" class="btn btn-sm btn-warning ml-2" @click="modalTC = true">
          Cargar TC
        </button>
      </template>
    </div>

    <!-- Alertas: una sola tarjeta en vez de tres barras. Cada una dice poco y
         juntas ocupaban media pantalla. -->
    <div v-if="alertas.length" class="card custom-card dash-card-alertas mb-3">
      <div class="card-body p-3">
        <h3 class="dash-card-title">Alertas operativas</h3>
        <div v-for="a in alertas" :key="a.codigo" class="dash-alert" :class="'dash-alert--' + a.tipo">
          <i :class="iconoAlerta(a.tipo)" class="mr-2"></i>
          <span class="flex-grow-1">{{ a.mensaje }}</span>
          <span class="dash-alert-count">{{ a.cantidad }}</span>
        </div>
      </div>
    </div>

    <!-- ══ HOY ══ -->
    <div class="dash-section">Hoy — {{ hoyLegible }}</div>

    <div class="row mb-2">
      <div v-for="k in kpisHoy" :key="k.label" class="col-6 col-lg-3 mb-2">
        <div class="card custom-card h-100">
          <div class="card-body p-3">
            <span class="dash-kpi-label">{{ k.label }}</span>
            <div class="d-flex align-items-baseline flex-wrap">
              <span v-if="k.bloqueado" class="dash-kpi-value dash-locked"><i class="ri-lock-line"></i></span>
              <span v-else class="dash-kpi-value" :class="k.color">{{ k.valor }}</span>
              <span v-if="k.delta" class="dash-delta ml-2" :class="k.deltaClase"
                v-b-tooltip.hover title="vs mes anterior">{{ k.delta }}</span>
            </div>
            <span v-if="k.sub" class="dash-kpi-sub">{{ k.sub }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-2">
      <div class="col-lg-4 mb-2">
        <div class="card custom-card h-100">
          <div class="card-body p-3">
            <h3 class="dash-card-title">Viajes por fuente — hoy</h3>
            <table v-if="today.viajes_por_fuente.length" class="table dash-table mb-0">
              <thead>
                <tr><th>Fuente</th><th class="text-right">Viajes</th><th class="text-right">Monto</th></tr>
              </thead>
              <tbody>
                <tr v-for="(f, i) in today.viajes_por_fuente" :key="i">
                  <td><span class="dash-dot" :style="{ background: colorSerie(i) }"></span>{{ etiqueta(f) }}</td>
                  <td class="text-right">{{ f.cantidad }}</td>
                  <td class="text-right">{{ f.monto === null ? '—' : money(f.monto) }}</td>
                </tr>
                <tr class="dash-total">
                  <td>TOTAL</td>
                  <td class="text-right">{{ today.viajes_hoy.total }}</td>
                  <td class="text-right">{{ today.ingresos_hoy.total === null ? '—' : money(today.ingresos_hoy.total) }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else class="dash-empty mb-0">Sin viajes hoy</p>
          </div>
        </div>
      </div>

      <div class="col-lg-4 mb-2">
        <div class="card custom-card h-100">
          <div class="card-body p-3">
            <h3 class="dash-card-title">Estado — hoy</h3>
            <div v-for="e in estadosHoy" :key="e.label" class="dash-row">
              <span><span class="dash-dot" :style="{ background: e.color }"></span>{{ e.label }}</span>
              <b :style="{ color: e.color }">{{ e.valor }}</b>
            </div>
            <h3 class="dash-card-title mt-3">Viajes próximos</h3>
            <div class="row text-center">
              <div v-for="u in proximos" :key="u.label" class="col-4 px-1">
                <div class="border rounded py-2">
                  <div class="dash-upcoming-value">{{ u.valor }}</div>
                  <div class="dash-kpi-sub">{{ u.label }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-4 mb-2">
        <div class="card custom-card h-100">
          <div class="card-body p-3">
            <h3 class="dash-card-title">Conductores</h3>
            <div v-for="d in conductores" :key="d.label" class="dash-row">
              <span><span class="dash-dot" :style="{ background: d.color }"></span>{{ d.label }}</span>
              <b :style="{ color: d.color }">{{ d.valor }}</b>
            </div>
            <p class="dash-kpi-sub mt-2 mb-0">No incluye conductores de transportista: no usan la app.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ MES ══ -->
    <div class="dash-section">Mes — {{ mesesNombres[month - 1] }} {{ year }}</div>

    <div class="row mb-1">
      <div v-for="k in kpisMes" :key="k.label" class="col-6 col-lg mb-2">
        <div class="card custom-card h-100">
          <div class="card-body p-3">
            <span class="dash-kpi-label">{{ k.label }}</span>
            <div class="d-flex align-items-baseline flex-wrap">
              <span v-if="k.bloqueado" class="dash-kpi-value dash-locked"><i class="ri-lock-line"></i></span>
              <span v-else class="dash-kpi-value" :class="k.color">{{ k.valor }}</span>
              <span v-if="k.delta" class="dash-delta ml-2" :class="k.deltaClase"
                v-b-tooltip.hover title="vs mes anterior">{{ k.delta }}</span>
            </div>
            <span v-if="k.sub" class="dash-kpi-sub">{{ k.sub }}</span>
          </div>
        </div>
      </div>
    </div>
    <p class="dash-kpi-sub mb-3">{{ etiquetaTC(mes.conversion) }}</p>

    <div class="card custom-card mb-2">
      <div class="card-body p-3">
        <h3 class="dash-card-title">Viajes por día</h3>
        <div class="dash-chart">
          <bar-chart v-if="hayDatosMes" :chart-data="chartViajesDia" :options="opcionesBarras" :height="270" />
          <p v-else class="dash-empty mb-0">Sin viajes en el período</p>
        </div>
      </div>
    </div>

    <div class="row mb-2">
      <div v-for="g in donuts" :key="g.titulo" class="col-lg-4 mb-2">
        <div class="card custom-card h-100">
          <div class="card-body p-3">
            <h3 class="dash-card-title">{{ g.titulo }}</h3>
            <div class="dash-chart dash-chart--sm">
              <doughnut-chart v-if="g.hay" :chart-data="g.data" :options="opcionesDonut" :height="160" />
              <p v-else class="dash-empty mb-0">Sin datos</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card custom-card mb-2">
      <div class="card-body p-3">
        <h3 class="dash-card-title">Ventas registradas del mes</h3>
        <p class="dash-kpi-sub mb-2">Por fecha de compra, no de viaje.</p>
        <div class="dash-chart">
          <bar-chart v-if="hayDatosVentas" :chart-data="chartVentasDia" :options="opcionesBarras" :height="270" />
          <p v-else class="dash-empty mb-0">Sin ventas en el período</p>
        </div>
      </div>
    </div>

    <div class="card custom-card">
      <div class="card-body p-3">
        <h3 class="dash-card-title">Ventas por precio × fuente × vehículo</h3>
        <div class="table-responsive">
          <table v-if="ventas.ventas_por_precio_fuente.length" class="table dash-table mb-0">
            <thead>
              <tr>
                <th>Fuente</th><th>Vehículo</th>
                <th class="text-right">Precio</th>
                <th class="text-right">Cantidad</th>
                <th class="text-right">Total</th>
                <th class="text-right">Equivale a</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(v, i) in ventas.ventas_por_precio_fuente" :key="i">
                <td><span class="badge badge-fuente" :class="'badge-fuente--' + badge(v.fuente)">{{ v.fuente }}</span></td>
                <td>{{ v.tipo_vehiculo }}</td>
                <td class="text-right">{{ montoConMoneda(v.precio, v.moneda) }}</td>
                <td class="text-right">{{ v.cantidad }}</td>
                <td class="text-right">{{ montoConMoneda(v.total, v.moneda) }}</td>
                <td class="text-right text-muted">{{ v.total_soles === null ? '—' : money(v.total_soles) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="dash-empty mb-0">Sin ventas en el período</p>
        </div>
      </div>
    </div>

    <!-- El mismo modal del mantenedor. Al guardar se recargan los datos, no la
         pagina. -->
    <exchange-rate-modal v-model="modalTC" @saved="loadAll" />
  </div>
</template>

<script>
import { Bar, Doughnut } from "vue-chartjs";
import { mapGetters } from "vuex";
import { DashboardService } from "@/core/services";
import { fuenteLabel, fuenteBadgeClass } from "@/core/utils/fuente";
import ExchangeRateModal from "@/core/components/ExchangeRateModal.vue";

// Mismo patrón que el dashboard del portal de afiliados: vue-chartjs 3 pide
// extender el tipo y llamar a renderChart.
const BarChart = {
  extends: Bar,
  props: ["chartData", "options"],
  watch: { chartData() { this.renderChart(this.chartData, this.options); } },
  mounted() { this.renderChart(this.chartData, this.options); },
};
const DoughnutChart = {
  extends: Doughnut,
  props: ["chartData", "options"],
  watch: { chartData() { this.renderChart(this.chartData, this.options); } },
  mounted() { this.renderChart(this.chartData, this.options); },
};

// Chart.js no lee Stylus, asi que la paleta se repite aca con los mismos
// hexes que las variables de wize-admin (color-blue, color-green, color-violet).
const COLOR = {
  azul: "#1A73E8",       // color-blue
  verde: "#27AE60",      // color-green
  ambar: "#F29C1A",      // color-alert-yellow
  violeta: "#6462EF",    // color-purple
  rojo: "#D32F2F",       // color-error
  cielo: "#5396EE",      // color-light-blue
  gris: "#9F9F9F",       // color-gray-secondary
};
const COLORES = [COLOR.azul, COLOR.verde, COLOR.ambar, COLOR.violeta, COLOR.rojo, COLOR.cielo, COLOR.gris];
const REFRESCO_MS = 5 * 60 * 1000;

// Respuestas vacías: la pantalla se dibuja con ceros desde el primer frame y
// nunca con null, así que una ciudad sin viajes muestra el dashboard en cero y
// no un error ni un gráfico roto.
const vacioToday = () => ({
  viajes_hoy: { total: 0, finalizados: 0, pendientes: 0, en_curso: 0, cancelados: 0, no_show: 0, otros: 0 },
  ingresos_hoy: { total: null, promedio_por_viaje: null, moneda: "PEN" },
  ventas_hoy: { total_reservas: 0, monto_total: null, moneda: "PEN", por_fuente: [] },
  viajes_por_fuente: [],
  conversion: { disponible: false, motivo: "SIN_TC", dias_arrastrados: 0, sin_tc: 0 },
});
const vacioMes = () => ({
  kpis: { viajes_total: 0, ingresos_total: null, cancelaciones: 0, tasa_cancelacion: 0, ticket_promedio: null, viajes_promedio_dia: null, moneda: "PEN" },
  comparativa_mes_anterior: { viajes_delta_pct: null, ingresos_delta_pct: null, cancelaciones_delta: 0, ticket_promedio_delta_pct: null },
  viajes_por_dia: [], distribucion_fuente: [], distribucion_tipo_viaje: [], distribucion_vehiculo: [],
  conversion: { disponible: false, motivo: "SIN_TC", dias_arrastrados: 0, sin_tc: 0 },
});
const vacioVentas = () => ({
  ventas_por_dia: [], ventas_por_precio_fuente: [],
  conversion: { disponible: false, motivo: "SIN_TC", dias_arrastrados: 0, sin_tc: 0 },
});

export default {
  name: "DashboardPage",
  components: { BarChart, DoughnutChart, ExchangeRateModal },
  data() {
    const ahora = new Date();
    return {
      // Lo resuelve mounted con la lista del API, antes de la primera
      // consulta: si saliera en null el dashboard pediría el total de las tres
      // ciudades y recién después filtraría.
      cityId: null,
      year: ahora.getFullYear(),
      month: ahora.getMonth() + 1,
      loading: false,
      horaActualizacion: "",
      timer: null,
      sinCiudades: false,
      errorCarga: false,
      today: vacioToday(),
      mes: vacioMes(),
      ventas: vacioVentas(),
      upcoming: { manana: 0, proximos_3_dias: 0, proximos_7_dias: 0 },
      drivers: { activos: 0, asignados_hoy: 0, disponibles: 0, inactivos: 0 },
      alertas: [],
      modalTC: false,
      mesesNombres: ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
        "Julio", "Agosto", "Setiembre", "Octubre", "Noviembre", "Diciembre"],
    };
  },
  computed: {
    ...mapGetters({ cities: "city/getCities" }),
    // Las ciudades activas, tal como vienen del API. Ya no hay "Todas": tres
    // ciudades con monedas y volúmenes distintos mezcladas no dicen nada. El
    // endpoint igual sigue aceptando city_id ausente como "todas", solo que
    // la pantalla no lo ofrece.
    ciudadesFiltro() {
      return this.cities || [];
    },
    hoyLegible() {
      const d = new Date();
      return `${d.getDate()} de ${this.mesesNombres[d.getMonth()].toLowerCase()}`;
    },
    // Los KPI se arman acá y no en el template: son ocho tarjetas con la misma
    // forma, y repetir el marcado era lo que las dejaba desalineadas entre sí.
    kpisHoy() {
      const t = this.today;
      return [
        {
          label: "Viajes hoy",
          color: "is-accent",
          valor: t.viajes_hoy.total,
          sub: `${t.viajes_hoy.finalizados} finalizados · ${t.viajes_hoy.pendientes} pendientes`,
        },
        {
          label: "Ingresos hoy",
          color: "is-green",
          bloqueado: t.ingresos_hoy.total === null,
          valor: this.money(t.ingresos_hoy.total),
          sub: t.ingresos_hoy.promedio_por_viaje === null
            ? "Sin tipo de cambio"
            : `${this.money(t.ingresos_hoy.promedio_por_viaje)} por viaje`,
        },
        {
          label: "Ventas hoy",
          color: "is-purple",
          valor: t.ventas_hoy.total_reservas,
          sub: "Reservas registradas hoy",
        },
        {
          label: "Monto vendido hoy",
          color: "is-amber",
          bloqueado: t.ventas_hoy.monto_total === null,
          valor: this.money(t.ventas_hoy.monto_total),
          sub: this.etiquetaTC(t.conversion),
        },
      ];
    },
    kpisMes() {
      const k = this.mes.kpis;
      const c = this.mes.comparativa_mes_anterior;
      return [
        {
          label: "Viajes",
          color: "is-accent",
          valor: k.viajes_total,
          delta: this.delta(c.viajes_delta_pct),
          deltaClase: this.deltaClase(c.viajes_delta_pct),
          sub: "",
        },
        {
          label: "Ingresos",
          color: "is-green",
          bloqueado: k.ingresos_total === null,
          valor: this.money(k.ingresos_total),
          delta: this.delta(c.ingresos_delta_pct),
          deltaClase: this.deltaClase(c.ingresos_delta_pct),
          sub: k.ticket_promedio === null ? "" : `Ticket promedio ${this.money(k.ticket_promedio)}`,
        },
        {
          label: "Cancelaciones",
          color: "is-red",
          valor: k.cancelaciones,
          sub: `Tasa: ${k.tasa_cancelacion}% del total`,
        },
        {
          label: "Ticket promedio",
          color: "is-purple",
          bloqueado: k.ticket_promedio === null,
          valor: this.money(k.ticket_promedio),
          delta: this.delta(c.ticket_promedio_delta_pct),
          deltaClase: this.deltaClase(c.ticket_promedio_delta_pct),
          // El API da el porcentaje de variacion, no el monto del mes
          // anterior: el mockup mostraba "Sep: S/ 138" y ese dato no existe.
          sub: "",
        },
        {
          // Viajes efectivamente ejecutados (finalizados y no-show) sobre los
          // días que ya cerraron. Es null el día 1 del mes, cuando no hay
          // ningún día cerrado para dividir.
          label: "Viajes promedio",
          color: "is-amber",
          valor: k.viajes_promedio_dia === null || k.viajes_promedio_dia === undefined
            ? "—"
            : k.viajes_promedio_dia,
          sub: this.esMesEnCurso ? "Por día, hasta ayer" : "Por día del mes",
        },
      ];
    },
    // El denominador del promedio cambia segun el mes: en el actual llega hasta
    // ayer, en uno pasado son todos los dias.
    esMesEnCurso() {
      const hoy = new Date();
      return this.year === hoy.getFullYear() && this.month === hoy.getMonth() + 1;
    },
    // Un no-show no es una cancelación: el conductor fue y el vehículo se ocupó,
    // lo que falló es que el pasajero no apareció. Por eso va en su propia fila.
    estadosHoy() {
      const v = this.today.viajes_hoy;
      const filas = [
        { label: "Pendientes", valor: v.pendientes, color: COLOR.ambar },
        { label: "En curso", valor: v.en_curso, color: COLOR.azul },
        { label: "Finalizados", valor: v.finalizados, color: COLOR.verde },
        { label: "Cancelados", valor: v.cancelados, color: COLOR.rojo },
        { label: "No-show", valor: v.no_show, color: COLOR.violeta },
      ];
      if (v.otros) filas.push({ label: "Otros", valor: v.otros, color: COLOR.gris });
      return filas;
    },
    proximos() {
      const u = this.upcoming;
      return [
        { label: "Mañana", valor: u.manana },
        { label: "3 días", valor: u.proximos_3_dias },
        { label: "7 días", valor: u.proximos_7_dias },
      ];
    },
    conductores() {
      const d = this.drivers;
      return [
        { label: "Activos", valor: d.activos, color: COLOR.verde },
        { label: "Asignados hoy", valor: d.asignados_hoy, color: COLOR.azul },
        { label: "Disponibles", valor: d.disponibles, color: COLOR.ambar },
        { label: "Inactivos", valor: d.inactivos, color: COLOR.gris },
      ];
    },
    // Los tres donuts son la misma tarjeta repetida: el v-for evita tres
    // bloques de marcado idénticos que solo cambian el título.
    donuts() {
      return [
        { titulo: "Por fuente", hay: this.mes.distribucion_fuente.length > 0, data: this.chartFuente },
        { titulo: "Viajes por tipo", hay: this.mes.distribucion_tipo_viaje.length > 0, data: this.chartTipoViaje },
        { titulo: "Por tipo de vehículo", hay: this.mes.distribucion_vehiculo.length > 0, data: this.chartVehiculo },
      ];
    },
    years() {
      const y = new Date().getFullYear();
      return [y - 2, y - 1, y, y + 1];
    },
    hayConversion() {
      return !!(this.today.conversion && this.today.conversion.disponible);
    },
    // El backend distingue "no hay TC cargado" de "la consulta falló"
    // (MotivoErrorTC). Son problemas distintos y se arreglan distinto.
    fallaTC() {
      return !!(this.today.conversion && this.today.conversion.motivo === "ERROR");
    },
    hayDatosMes() {
      return this.mes.viajes_por_dia.some((d) => d.total > 0);
    },
    hayDatosVentas() {
      return this.ventas.ventas_por_dia.some((d) => d.total > 0);
    },
    chartViajesDia() {
      return this.barrasApiladas(this.mes.viajes_por_dia);
    },
    chartVentasDia() {
      return this.barrasApiladas(this.ventas.ventas_por_dia);
    },
    chartFuente() {
      return this.donut(
        this.mes.distribucion_fuente.map((f) => `${f.fuente} (${f.porcentaje}%)`),
        this.mes.distribucion_fuente.map((f) => f.cantidad)
      );
    },
    // Cuenta viajes, no factura. El porcentaje sale del backend sobre el total
    // del período, igual que el dónut de tipo de vehículo.
    chartTipoViaje() {
      return this.donut(
        this.mes.distribucion_tipo_viaje.map((t) => `${t.label} (${t.porcentaje}%)`),
        this.mes.distribucion_tipo_viaje.map((t) => t.cantidad)
      );
    },
    chartVehiculo() {
      return this.donut(
        this.mes.distribucion_vehiculo.map((v) => `${v.tipo} (${v.porcentaje}%)`),
        this.mes.distribucion_vehiculo.map((v) => v.cantidad)
      );
    },
    opcionesBarras() {
      // El día completo en un solo tooltip, no el segmento bajo el cursor.
      //
      // Chart.js acá es 2.9: la clave es `tooltips.mode`, no el
      // `interaction: { mode }` de la 3, que esta versión ignora en silencio.
      // `hover` va igual para que la columna entera responda y no haya que
      // apuntarle a un segmento.
      const valor = (item, data) => Number(data.datasets[item.datasetIndex].data[item.index]) || 0;
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: "bottom" },
        hover: { mode: "index", intersect: false },
        tooltips: {
          mode: "index",
          intersect: false,
          // Una fuente sin viajes ese día no aporta: con las cuatro siempre
          // listadas, la mitad del tooltip es "DIRECTO: 0".
          filter: (item, data) => valor(item, data) > 0,
          callbacks: {
            footer: (items, data) => {
              const total = items.reduce((a, i) => a + valor(i, data), 0);
              return `TOTAL: ${total}`;
            },
          },
        },
        scales: {
          xAxes: [{ stacked: true, gridLines: { display: false } }],
          yAxes: [{ stacked: true, ticks: { beginAtZero: true, precision: 0 } }],
        },
      };
    },
    opcionesDonut() {
      return { responsive: true, maintainAspectRatio: false, legend: { position: "bottom" } };
    },
  },
  methods: {
    // La etiqueta sale del util compartido con la lista de Viajes: una sola
    // implementación para las dos pantallas (DEF-ADM-028).
    etiqueta(f) {
      return fuenteLabel(f.fuente, f.tipo_pasajero);
    },
    colorSerie(i) {
      return COLORES[i % COLORES.length];
    },
    badge(fuente) {
      return fuenteBadgeClass(fuente);
    },
    money(v) {
      if (v === null || v === undefined) return "—";
      const n = Number(v);
      return `S/ ${n.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    // Etiqueta permanente: nadie debe confundir un monto convertido con uno
    // facturado. Suma el aviso de arrastre, que es informativo y no alerta.
    etiquetaTC(c) {
      if (!c || !c.disponible) return "";
      const partes = ["Convertido a S/ con TC de compra SUNAT"];
      if (c.dias_arrastrados > 0) {
        const d = c.dias_arrastrados;
        partes.push(`${d} ${d === 1 ? "día usa" : "días usan"} tipo de cambio arrastrado`);
      }
      if (c.sin_tc > 0) {
        const n = c.sin_tc;
        partes.push(`${n} ${n === 1 ? "viaje anterior" : "viajes anteriores"} al TC más antiguo no entran en los montos`);
      }
      return partes.join(" · ");
    },
    montoConMoneda(v, moneda) {
      const n = Number(v || 0);
      const simbolo = moneda === "SOLES" ? "S/" : "US$";
      return `${simbolo} ${n.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    // null = el mes anterior fue cero y no hay variación que mostrar.
    delta(v) {
      if (v === null || v === undefined) return "";
      return `${v > 0 ? "+" : ""}${v}%`;
    },
    deltaClase(v) {
      if (v === null || v === undefined) return "";
      return v >= 0 ? "is-up" : "is-down";
    },
    iconoAlerta(tipo) {
      return { critical: "ri-error-warning-fill", warning: "ri-alert-fill", info: "ri-information-fill" }[tipo] || "ri-information-fill";
    },
    barrasApiladas(dias) {
      const etiquetas = dias.map((d) => d.dia.slice(8));
      const serie = (clave, color) => ({
        label: clave.toUpperCase(),
        backgroundColor: color,
        data: dias.map((d) => d[clave]),
      });
      return {
        labels: etiquetas,
        datasets: [
          serie("despegar", COLORES[0]),
          serie("polittan", COLORES[1]),
          serie("directo", COLORES[2]),
          serie("otras", COLORES[6]),
        ],
      };
    },
    donut(labels, data) {
      return {
        labels,
        datasets: [{ data, backgroundColor: COLORES.slice(0, data.length), borderWidth: 0 }],
      };
    },
    // Lima por defecto. Si no estuviera entre las activas, la primera que
    // venga, para que la pantalla nunca quede sin ciudad seleccionada.
    ciudadPorDefecto() {
      const lista = this.cities || [];
      const sinTildes = (t) => (t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
      const lima = lista.find((c) => sinTildes(c.name) === "lima");
      if (lima) return lima.id;
      return lista.length ? lista[0].id : null;
    },
    // Elige la ciudad y recién ahí consulta. Sin ciudades no consulta nada:
    // una respuesta sin city_id son las tres ciudades mezcladas.
    async arrancar() {
      if (!this.cities || !this.cities.length) {
        await this.$store.dispatch("city/loadCities").catch(() => {});
      }
      this.cityId = this.ciudadPorDefecto();
      this.sinCiudades = this.cityId === null;
      if (this.sinCiudades) return;
      await this.loadAll();
    },
    async reintentarCiudades() {
      this.loading = true;
      await this.$store.dispatch("city/loadCities").catch(() => {});
      this.loading = false;
      await this.arrancar();
    },
    setCity(id) {
      this.cityId = id;
      this.loadAll();
    },
    loadPeriodo() {
      this.loadAll();
    },
    async loadAll() {
      this.loading = true;
      try {
        const c = this.cityId;
        const [today, mes, ventas, upcoming, drivers, alerts] = await Promise.all([
          DashboardService.today(c),
          DashboardService.month(c, this.year, this.month),
          DashboardService.ventasMes(c, this.year, this.month),
          DashboardService.upcoming(c),
          DashboardService.drivers(c),
          DashboardService.alerts(c),
        ]);
        this.today = today.payload || vacioToday();
        this.mes = mes.payload || vacioMes();
        this.ventas = ventas.payload || vacioVentas();
        this.upcoming = upcoming.payload || this.upcoming;
        this.drivers = drivers.payload || this.drivers;
        this.alertas = (alerts.payload && alerts.payload.alertas) || [];
        this.horaActualizacion = new Date().toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" });
        this.errorCarga = false;
      } catch (e) {
        // No se pisa lo que ya estaba en pantalla, pero se avisa: los datos
        // viejos sin aviso son peores que no tener datos.
        console.error("Error al cargar el dashboard", e);
        this.errorCarga = true;
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    // Primero la ciudad, después los datos: al revés se vería por un instante
    // el total de todas las ciudades antes de filtrar.
    await this.arrancar();
    this.timer = setInterval(() => {
      if (!this.sinCiudades) this.loadAll();
    }, REFRESCO_MS);
  },
  beforeDestroy() {
    // Sin esto el intervalo sigue pegándole al API después de salir.
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  },
};
</script>

<style lang="stylus" scoped>
// La paleta sale de wize-admin, no de hexes propios: si el dashboard inventa
// sus colores deja de parecerse al resto del admin.
@import "~wize-admin/dist/styles/variables"

// Las tarjetas, la grilla, las tablas y los badges son de Bootstrap y de
// wize-admin (.card, .custom-card, .row/.col, .table, .badge). Lo que queda
// acá es solo lo que el sistema no trae: la tipografía de un KPI, la fila de
// alerta y el rótulo de sección.

// Rótulo de sección con la línea que lo acompaña, como en el mockup.
.dash-section
  display flex
  align-items center
  gap 10px
  font-size 12px
  font-weight 600
  text-transform uppercase
  letter-spacing 0.8px
  color color-grey-1
  margin 4px 0 10px
  &::after
    content ''
    flex 1
    height 1px
    background color-lead

.dash-card-title
  font-size 12px
  font-weight 600
  text-transform uppercase
  letter-spacing 0.5px
  color color-grey-1
  margin 0 0 10px

// ── KPI ────────────────────────────────────────────────────────────────
.dash-kpi-label
  display block
  font-size 11px
  font-weight 600
  text-transform uppercase
  letter-spacing 0.6px
  color color-grey-1

.dash-kpi-value
  font-size 26px
  font-weight 700
  white-space nowrap
  flex-shrink 0
  line-height 1.3
  letter-spacing -0.5px
  color color-black-2
  &.is-accent
    color color-blue
  &.is-green
    color color-green
  &.is-amber
    color color-alert-yellow
  &.is-purple
    color color-purple
  &.is-red
    color color-error

.dash-kpi-sub
  display block
  font-size 11px
  color color-gray-secondary
  margin-top 2px

.dash-locked
  color color-gray-2

.dash-delta
  font-size 11px
  font-weight 600
  padding 1px 6px
  border-radius 4px
  white-space nowrap
  cursor default
  &.is-up
    background rgba(color-green, 0.12)
    color color-green
  &.is-down
    background rgba(color-error, 0.12)
    color color-error

// ── Alertas ────────────────────────────────────────────────────────────
// Una sola tarjeta en lugar de tres barras: cada alerta dice poco y juntas
// empujaban todo el dashboard fuera de la primera pantalla.
.dash-card-alertas
  border-left 3px solid color-error

.dash-alert
  display flex
  align-items center
  padding 6px 10px
  border-radius 4px
  font-size 13px
  margin-bottom 5px
  &:last-child
    margin-bottom 0
  &--critical
    background color-bg-error
    color color-error
  &--warning
    background rgba(color-alert-yellow, 0.14)
    color darken(color-alert-yellow, 25%)
  &--info
    background rgba(color-blue, 0.08)
    color color-blue

.dash-alert-count
  font-weight 700
  font-size 17px
  margin-left 10px

// ── Filas de Estado, Próximos y Conductores ────────────────────────────
.dash-row
  display flex
  justify-content space-between
  align-items center
  padding 5px 0
  font-size 13px
  border-bottom 1px solid color-lead
  &:last-of-type
    border-bottom none

.dash-dot
  display inline-block
  width 8px
  height 8px
  border-radius 50%
  margin-right 8px
  vertical-align middle

.dash-upcoming-value
  font-size 22px
  font-weight 700
  line-height 1.2
  color color-blue

// ── Tablas y gráficos ──────────────────────────────────────────────────
.dash-table
  font-size 13px
  th
    font-size 11px
    font-weight 600
    text-transform uppercase
    letter-spacing 0.5px
    color color-grey-1
    border-top none
    border-bottom 1px solid color-gray-2
    padding 0 6px 8px
  td
    padding 6px
    border-top none
    border-bottom 1px solid color-lead
    vertical-align middle
  tbody tr:last-child td
    border-bottom none

.dash-total td
  font-weight 700
  border-top 1px solid color-gray-2 !important

.dash-chart
  position relative
  min-height 270px
  &--sm
    min-height 160px

.dash-empty
  color color-gray-secondary
  font-size 13px
  text-align center
  padding 26px 0

.badge-fuente
  font-size 10px
  padding 3px 7px
  color color-white
  font-weight 600
  background-color color-grey-1
  &--despegar
    background-color color-blue
  &--polittan
    background-color color-green
  &--directo
    background-color color-alert-yellow
  &--elife
    background-color color-purple
  &--viator
    background-color color-light-blue
  &--web
    background-color color-violet
</style>
